import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { sectionWidthField, universalFields, MEDIA_RADIUS_PX } from '../_shared/universal.js';
import { DESKTOP_GROUP, MOBILE_GROUP } from '../_shared/device.js';
import { headingBlockSchema } from '../../blocks/heading/index.js';
import { textBlockSchema } from '../../blocks/text/index.js';
import { buttonBlockSchema } from '../../blocks/button/index.js';
import { dividerBlockSchema } from '../../blocks/divider/index.js';
import { ratingBlockSchema } from '../../blocks/rating/index.js';
import { imageBlockSchema } from '../../blocks/image/index.js';
import { iconWithTextBlockSchema } from '../../blocks/icon-with-text/index.js';
import { textWithIconBlockSchema } from '../../blocks/text-with-icon/index.js';
import { iconWithContentBlockSchema } from '../../blocks/icon-with-content/index.js';
/**
 * image-with-text — Shrine's COMPOSABLE media + content section. Like rich-text, the content side is now a
 * CONTAINER of CHILD BLOCKS (`heading` / `text` / `button` / `divider` / `rating` / `image` + the icon
 * blocks), each with its own Shrine config — image-with-text simply adds a MEDIA column (image) beside that
 * content column. The section keeps only the split layout (image position / width / ratio / vertical align /
 * mobile order), an optional background pattern, and the universal band (colour scheme / padding / width).
 * Since 21/09/2026 the text block also has its own place: a distance from its edge on BOTH axes, a width and
 * a placement — on the desktop AND, as four twins under « Mobile », on the phone.
 *
 * v2 → v3: the model changed from monolithic heading/body/cta fields (+ a `feature` block) to a block
 * container. Old content keys are harmless leftovers (ignored by the container Component); a fresh instance
 * is seeded from the preset's image + heading + text + button blocks.
 */
const IWT_ACCEPTS = [
    'heading',
    'text',
    'button',
    'divider',
    'rating',
    'image',
    'icon-with-text',
    'text-with-icon',
    'icon-with-content',
];
/**
 * The four crops the media column offers, named once: the desktop select and the phone's own select
 * MUST offer the same shapes, or "Same as desktop" stops meaning anything.
 */
const IMAGE_RATIO_OPTIONS = [
    { value: 'adapt', label: 'Adapt to image' },
    { value: 'square', label: 'Square (1:1)' },
    { value: 'portrait', label: 'Portrait (4:5)' },
    { value: 'landscape', label: 'Landscape (3:2)' },
];
/**
 * Les trois largeurs d'avant le 21/09/2026, en pourcentage de la rangée. Exportée : le composant l'utilise
 * aussi, parce qu'une recette IA ou un document jamais rouvert peut encore lui tendre `'large'`.
 */
export function legacyImageWidthPercent(v) {
    if (typeof v === 'number')
        return v;
    if (v === 'small')
        return 33;
    if (v === 'large')
        return 67;
    return 50;
}
export const imageWithTextSchema = {
    type: 'image-with-text',
    name: 'Image with text',
    version: 5,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    accepts: [...IWT_ACCEPTS],
    // The block SCHEMAS the content column can instantiate (the same global blocks listed in `accepts`).
    blocks: [
        headingBlockSchema,
        textBlockSchema,
        buttonBlockSchema,
        dividerBlockSchema,
        ratingBlockSchema,
        imageBlockSchema,
        iconWithTextBlockSchema,
        textWithIconBlockSchema,
        iconWithContentBlockSchema,
    ],
    maxBlocks: 20,
    fields: [
        // ── Layout ───────────────────────────────────────────────────────────────
        {
            // Ajouté 2026-08-20 : sans lui cette section n'a PAS d'ancre HTML, donc une entrée d'`anchor-nav`
            // qui la vise ne défile nulle part — un lien bien formé et parfaitement mort, qu'aucun test « pas
            // de href nu » ne voit. Seules quatre sections de la bibliothèque le déclaraient ; la barre de
            // sommaire de Roast a dû être amputée de moitié pour cette seule raison.
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            group: 'General',
            info: 'Adds an anchor id to this section so a link can scroll to it.',
            default: false,
        },
        // ── Media — the two files, read by both devices ──────────────────────────
        // The picture (and the clip) live ABOVE the two device parts because they are what the media IS,
        // not how it looks: a section that never opens the Mobile part shows this one everywhere. Same
        // shape as the `image` and `video` sections since 18/09.
        { id: 'image', type: 'image', label: 'Image', group: 'Media' },
        // A video (spec 0020 upload: MP4/WebM + poster) shown in the media column. When set it REPLACES the
        // image and ALWAYS plays exactly like one — silent autoplay loop, NO player chrome — so image and video
        // are visually identical (founder: "it should be like an image, not a video player"). No playback toggles
        // on purpose: an image has none either. Native <video>; SSR emits `muted` so autoplay works with no PE
        // module. The ONE section powers both "Image with text" and "Video with text" (two registrations/presets).
        {
            id: 'video',
            type: 'video',
            label: 'Video',
            group: 'Media',
            info: 'Upload an MP4/WebM. When set, it replaces the image and plays inline like one — silent, looping, no play bar.',
        },
        // Une seule case pour le clip ordinateur ET le clip mobile : c'est le même plan sur deux appareils.
        playButtonField({
            defaultOn: false,
            group: 'Media',
            visibleWhen: { field: 'video', empty: false },
        }),
        // ── Desktop ──────────────────────────────────────────────────────────────
        // The split itself: where the media sits, how much room it takes, the gutter, the crop. Every one of
        // these was already desktop-only; what changed on 18/09 is that they now say so by WHERE they are
        // instead of by a "Desktop …" prefix on each label (founder: "j'ai du mal à chercher les config").
        {
            id: 'layout',
            type: 'select',
            label: 'Image position',
            group: DESKTOP_GROUP,
            default: 'start',
            options: [
                { value: 'start', label: 'Image first (left)' },
                { value: 'end', label: 'Image last (right)' },
            ],
        },
        {
            // v4 (21/09/2026) : un CURSEUR en pourcentage, plus trois tailles. « Je ne peux pas positionner
            // l'image et le texte aussi librement que je veux » (fondatrice) : Petit/Moyen/Grand ne donnait que
            // trois frontières possibles entre les deux colonnes. La part de l'image est maintenant un nombre,
            // et le texte prend TOUT LE RESTE — c'est ce qui rend les deux curseurs de distance ci-dessous
            // indépendants l'un de l'autre. Les anciennes valeurs `small`/`medium`/`large` sont migrées en
            // 33/50/67 (`migrate`), et le composant les lit encore telles quelles au cas où un document
            // non migré passe (recette IA, instance jamais rouverte).
            id: 'imageWidth',
            type: 'range',
            label: 'Image width',
            group: DESKTOP_GROUP,
            min: 10,
            max: 90,
            step: 1,
            unit: '%',
            default: 50,
            info: 'Share of the row the picture takes. The text gets all the rest.',
        },
        {
            // L'ÉCART entre les deux colonnes, séparé de leur LARGEUR (fondatrice, 16/09/2026). « Large » déplace la
            // frontière image/texte ; il ne dit rien de la gouttière, restée 48 px en dur. Et le même écart ne se LIT
            // pas pareil selon le côté : image à gauche, le texte démarre juste après la gouttière et paraît collé ;
            // image à droite, c'est la fin des lignes (plus courtes que la colonne) qui borde la photo, donc le vide
            // paraît énorme. Un curseur est le seul moyen de rattraper cette asymétrie sans toucher aux largeurs.
            id: 'columnGapDesktop',
            type: 'range',
            label: 'Space between image and text',
            group: DESKTOP_GROUP,
            min: 0,
            max: 160,
            step: 4,
            unit: 'px',
            default: 48,
            info: 'Minimum room between the two columns. It is taken from the text side only — the picture keeps its width.',
        },
        {
            // LA DISTANCE DE L'IMAGE À SON BORD (fondatrice, 21/09/2026 : « positionner l'image tout à droite,
            // au centre, comme je veux »). L'écart ci-dessus ne bouge que la FRONTIÈRE entre les deux colonnes ;
            // rien ne permettait de décoller la photo du bord extérieur de la bande. Ce curseur la pousse vers le
            // texte, en marge, donc sans jamais recouvrir la colonne de texte — qui rétrécit d'autant.
            id: 'imageInset',
            type: 'range',
            label: 'Image: distance from the edge',
            group: DESKTOP_GROUP,
            min: 0,
            max: 400,
            step: 8,
            unit: 'px',
            default: 0,
            info: 'Pushes the picture away from the outer edge, toward the text. Only the picture moves.',
        },
        {
            // LA DISTANCE DU TEXTE À SON BORD — le réglage manquant que la fondatrice décrit (21/09/2026) : avec
            // l'image à droite, agrandir l'écart ne faisait bouger que l'image, parce que le texte reste calé sur
            // le bord gauche de sa colonne et que ses lignes, plus courtes qu'elle, ne touchent jamais la photo.
            // Ce curseur pousse le bloc de texte vers la photo. Seul le texte bouge.
            //
            // 21/09 soir : la distance se règle sur DEUX AXES (« distance from x axis and y axis on its own »).
            // Celui-ci est l'axe horizontal ; il GARDE son id `textInset` pour qu'un document déjà enregistré ne
            // perde rien. Son jumeau vertical est `textInsetY`, juste en dessous.
            id: 'textInset',
            type: 'range',
            label: 'Text: horizontal distance from the edge',
            group: DESKTOP_GROUP,
            min: 0,
            max: 400,
            step: 8,
            unit: 'px',
            default: 0,
            info: 'Pushes the text away from the outer side edge, toward the picture. Only the text moves.',
        },
        {
            // L'AXE VERTICAL. Le texte s'écarte du bord auquel « Alignement vertical » l'accroche : le haut, ou
            // le bas. Au milieu il n'y a pas de bord — la valeur le descend d'autant sous sa position centrée
            // (le composant compense la marge pour que le centrage ne mange pas la moitié du déplacement).
            id: 'textInsetY',
            type: 'range',
            label: 'Text: vertical distance from the edge',
            group: DESKTOP_GROUP,
            min: 0,
            max: 400,
            step: 8,
            unit: 'px',
            default: 0,
            info: 'Pushes the text down from the top edge — or up from the bottom edge when the vertical alignment is "Bottom". Only the text moves.',
        },
        {
            // La LARGEUR du bloc de texte dans sa colonne, puis son placement. À 100 % (défaut) le texte remplit
            // la colonne comme avant ; en dessous, « Text placement » dit où il se pose — c'est ce qui permet un
            // paragraphe centré entre le bord et la photo, ou collé à la photo, sans toucher à l'image.
            id: 'textWidth',
            type: 'range',
            label: 'Text width',
            group: DESKTOP_GROUP,
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 100,
            info: 'Share of its column the text block may fill. Below 100 %, "Text placement" says where it sits.',
        },
        {
            id: 'textPlacement',
            type: 'select',
            label: 'Text placement',
            group: DESKTOP_GROUP,
            default: 'start',
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'end', label: 'Right' },
            ],
            visibleWhen: { field: 'textWidth', notEquals: 100 },
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: DESKTOP_GROUP,
            default: 'adapt',
            options: [...IMAGE_RATIO_OPTIONS],
        },
        // L'ARRONDI DE L'IMAGE, comme sur la section « Image » (fondatrice, 24/09/2026 : « the image with
        // text component doesn't have border radius config like image component »). Un curseur en pixels par
        // appareil, dans la partie de l'appareil — plus le menu « Coins des images » à huit crans qui vivait
        // sous « Section ». `0` = le rayon du thème (Réglages du thème › Mise en page), jamais « carré ».
        {
            id: 'borderRadius',
            type: 'range',
            label: 'Corner radius',
            group: DESKTOP_GROUP,
            min: 0,
            max: 60,
            step: 2,
            unit: 'px',
            default: 0,
        },
        {
            id: 'verticalAlignment',
            type: 'select',
            label: 'Vertical alignment',
            group: DESKTOP_GROUP,
            default: 'middle',
            options: [
                { value: 'top', label: 'Top' },
                { value: 'middle', label: 'Middle' },
                { value: 'bottom', label: 'Bottom' },
            ],
        },
        // ── Mobile ───────────────────────────────────────────────────────────────
        // THE PHONE'S OWN MEDIA (fondatrice, 18/09/2026: « le composant image with text n'a pas de version
        // d'image et vidéo pour desktop et mobile »). Every control here means "same as desktop" while it is
        // untouched — an empty file, `inherit` on the select — so this lands on live storefronts without
        // repainting one page, and a seller who never opens this part keeps one design everywhere.
        //
        // The labels do NOT repeat the word "Mobile": the part they sit under already says it, and a label
        // that repeats its own heading is exactly the noise that made these settings hard to find.
        {
            id: 'mobileImage',
            type: 'image',
            label: 'Image',
            group: MOBILE_GROUP,
            info: 'Optional. Shown under 750px instead of the desktop image — leave empty to use the same one.',
        },
        {
            id: 'mobileVideo',
            type: 'video',
            label: 'Video',
            group: MOBILE_GROUP,
            info: 'Optional. Played under 750px instead of the desktop video — the place for a vertical cut. Leave empty to keep the same file.',
        },
        {
            // A crop is NOT a device-independent choice: the 3:2 that frames a desktop column leaves a letterbox
            // strip once the media goes full-width on a phone. `inherit` (the default) keeps the desktop answer.
            id: 'mobileImageRatio',
            type: 'select',
            label: 'Image ratio',
            group: MOBILE_GROUP,
            default: 'inherit',
            options: [{ value: 'inherit', label: 'Same as desktop' }, ...IMAGE_RATIO_OPTIONS],
        },
        {
            id: 'mobileBorderRadius',
            type: 'range',
            label: 'Corner radius',
            group: MOBILE_GROUP,
            min: 0,
            max: 60,
            step: 2,
            unit: 'px',
            inheritFrom: 'borderRadius',
        },
        {
            id: 'mobileLayout',
            type: 'select',
            // « Disposition mobile » sous un titre « Mobile » répète son propre en-tête — le bruit qu'on vient
            // de retirer partout ailleurs dans ce panneau.
            label: 'Order',
            group: MOBILE_GROUP,
            default: 'image-first',
            options: [
                { value: 'image-first', label: 'Image first' },
                { value: 'text-first', label: 'Text first' },
            ],
        },
        {
            // Sur mobile les colonnes s'empilent : le même réglage y devient un écart VERTICAL, et un seul curseur
            // pour les deux mentirait sur l'un des deux écrans. D'où un second, avec sa propre échelle.
            id: 'rowGapMobile',
            type: 'range',
            label: 'Space between image and text',
            group: MOBILE_GROUP,
            min: 0,
            max: 80,
            step: 4,
            unit: 'px',
            default: 32,
            info: 'Vertical gap once the two columns stack, below 750px.',
        },
        // ── Le TEXTE du téléphone (fondatrice, 21/09/2026) ────────────────────────
        // « Toutes les config qu'on fait pour le texte doivent être dupliquées, une pour mobile et une pour
        // desktop. » Les quatre réglages de texte de la partie Ordinateur ont donc leur jumeau ici. DEUX
        // contrats, et c'est le bouton de chaque curseur qui dit lequel :
        //  - les DISTANCES (px) : absent = AUCUNE — ce que le téléphone peignait déjà. Un décalage de 120 px
        //    pensé pour une rangée de 1 280 px n'a aucun sens sur 390 px, et une boutique en ligne ne bouge
        //    pas d'un pixel. La reprise part de 0 (`default`), pas du nombre du bureau (`inheritLabel` seul).
        //  - la LARGEUR et le PLACEMENT (%, choix) : absent = « comme sur ordinateur », le contrat des autres
        //    parties Mobile — un paragraphe à 60 % centré se transpose tel quel sur un téléphone.
        {
            id: 'mobileTextInset',
            type: 'range',
            label: 'Text: horizontal distance from the edge',
            group: MOBILE_GROUP,
            min: 0,
            max: 400,
            step: 8,
            unit: 'px',
            default: 0,
            inheritLabel: 'None — the text sits on the edge',
            info: 'Pushes the text away from the same side edge as on desktop. Only the phone changes.',
        },
        {
            id: 'mobileTextInsetY',
            type: 'range',
            label: 'Text: vertical distance from the edge',
            group: MOBILE_GROUP,
            min: 0,
            max: 400,
            step: 8,
            unit: 'px',
            default: 0,
            inheritLabel: 'None — the text sits on the edge',
            info: 'Pushes the text down, away from what sits above it: the picture, or the top edge when the text comes first. Only the phone changes.',
        },
        {
            id: 'mobileTextWidth',
            type: 'range',
            label: 'Text width',
            group: MOBILE_GROUP,
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            inheritFrom: 'textWidth',
            info: 'Share of the width the text block may fill on the phone. Below 100 %, "Text placement" says where it sits.',
        },
        {
            id: 'mobileTextPlacement',
            type: 'select',
            label: 'Text placement',
            group: MOBILE_GROUP,
            default: 'inherit',
            options: [
                { value: 'inherit', label: 'Same as desktop' },
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'end', label: 'Right' },
            ],
            // Tant que la largeur mobile n'a rien dit (absente), le placement reste visible : il peut avoir un
            // sens si l'ordinateur est sous 100 %. Il ne se cache que sur un 100 % explicite du téléphone.
            visibleWhen: { field: 'mobileTextWidth', notEquals: 100 },
        },
        // ── Background pattern (shared engine). ──────────────────────────────────────────────────────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'Background pattern',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'Background pattern',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'Background pattern',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'Background pattern',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'Background pattern',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'Background pattern',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'Background pattern',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Section (universal) ──────────────────────────────────────────────────
        sectionWidthField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    migrate(fromVersion, settings) {
        // v1/v2 were monolithic heading/body/cta fields (+ a `feature` block); v3 is a content-block container.
        // Old keys pass through (ignored by the container Component); blocks are seeded from the preset.
        if (fromVersion >= 5)
            return settings;
        // v3 → v4 : `imageWidth` passe de trois tailles à un pourcentage. Idempotent — un nombre reste un
        // nombre — parce que `hydrateDocument` migre sans retamponner la version, et `resolveSettings`
        // repasse donc ici à chaque rendu du canvas.
        const v4 = fromVersion >= 4
            ? settings
            : {
                ...settings,
                imageWidth: legacyImageWidthPercent(settings.imageWidth),
            };
        // v4 → v5 : le menu « Coins des images » (`mediaRadius`, huit crans) devient le curseur en pixels
        // `borderRadius`. Une section qui avait choisi « 8 px » garde ses 8 px ET son curseur les affiche —
        // sans ce report elle aurait peint 8 px avec un curseur à 0, et personne n'aurait su d'où ils
        // venaient. « Défaut du thème » et « Carré » deviennent 0 = le rayon du thème (carré d'origine).
        // Idempotent : la clé migrée est retirée, un second passage ne trouve rien à faire.
        const { mediaRadius, ...rest } = v4;
        if (mediaRadius === undefined)
            return v4;
        const px = typeof mediaRadius === 'string'
            ? MEDIA_RADIUS_PX[mediaRadius]
            : undefined;
        const borderRadius = typeof rest.borderRadius === 'number' ? rest.borderRadius : (px ?? 0);
        return { ...rest, borderRadius };
    },
};
export const imageWithTextPresets = [
    {
        name: 'Image with text',
        settings: {
            // No default image — a fresh instance ships EMPTY so the seller drops in their own; the empty media
            // column renders the neutral placeholder box (var(--aqx-scheme-surface)), not a stock demo photo.
            layout: 'start',
            imageWidth: 50,
            imageRatio: 'adapt',
            verticalAlignment: 'middle',
            // The phone starts with NO media and NO crop of its own: one design until the seller asks for two.
            mobileImageRatio: 'inherit',
            // LIGHT band by default — the section sits on the store's light background (seller flips to
            // inverse/accent per instance if they want a dark editorial break).
            colorScheme: 'light',
            paddingTop: 88,
            paddingBottom: 88,
            // WIDE content frame by default (like rich-text / logo-list): the split media + text layout needs
            // room to breathe — 'standard' squeezes the image column on a 1440 px screen.
            width: 'wide',
        },
        // Content column = a heading + paragraph + button, each a real child block (Shrine's image-with-text).
        blocks: [
            {
                type: 'heading',
                settings: {
                    title: { format: 'html', html: '@t:section.thought_through' },
                    headingSize: 'h1',
                    alignment: 'left',
                    mobileAlignment: 'mobile-center',
                },
            },
            {
                type: 'text',
                settings: {
                    text: {
                        format: 'html',
                        html: '<p>Des matières sélectionnées avec soin et des finitions soignées, pour des pièces qui durent saison après saison.</p>',
                    },
                    alignment: 'left',
                    mobileAlignment: 'center',
                },
            },
            {
                type: 'button',
                settings: {
                    button_label: '@t:common.learn_more',
                    alignment: 'flex-start',
                    mobile_alignment: 'center',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const imageWithTextMeta = {
    label: 'Image with text',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map